import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { GeneralParameters } from 'src/app/constants/general-parameters-constans';
import { QuotationStateEnums } from 'src/app/enum/quotation-enums';
import { StepPanelEnums } from 'src/app/enum/step-panel-enums';
import { ActionColor, CheckBoxAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InternalOrderModel } from 'src/app/models/shopping/internal-order.model';
import { BreakdownByDetailTableModel } from 'src/app/models/tables/preliminaries/breakdown-by-detail-table.model';
import { QuotationDetailSelectionTableModel } from 'src/app/models/tables/preliminaries/quotation-detail-selection-table.model';
import { QuotationSelectionTableModel } from 'src/app/models/tables/preliminaries/quotation-selection-table.model';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { BreakdownService } from 'src/app/services/preliminaries/breakdown.service';
import { QuotationDetailService } from 'src/app/services/preliminaries/quotation-detail.service';
import { QuotationService } from 'src/app/services/preliminaries/quotation.service';
import { InternalOrderDetailService } from 'src/app/services/shopping/internal-order-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-internal-order-from-quotation',
  templateUrl: './internal-order-from-quotation.component.html',
  styleUrls: ['./internal-order-from-quotation.component.scss'],
  standalone: false
})
export class InternalOrderFromQuotationComponent implements OnInit, OnDestroy {

  private readonly breakdownService = inject(BreakdownService);
  public readonly config = inject(DynamicDialogConfig);
  private readonly internalOrderDetailService = inject(InternalOrderDetailService);
  private readonly quotationDetailService = inject(QuotationDetailService);
  private readonly quotationService = inject(QuotationService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly userAuthenticatedService = inject(UserAuthenticatedService);
  private readonly ref = inject(DynamicDialogRef, { optional: true });

  ascendant: boolean = true;
  breakdownHeader: ColumnResponseModel[] = [];
  checkboxActions: CheckBoxAction[] = [
    { label: 'Agregar', icon: 'pi pi-plus-circle', color: ActionColor.SUCCESS, action: 'add' },
  ];
  columnSort: string | null = null;
  currentStep: number = StepPanelEnums.PanelOne;
  dataBreakdown: PagedResponseModel<BreakdownByDetailTableModel> =
    new PagedResponseModel<BreakdownByDetailTableModel>();
  dataQuotation: PagedResponseModel<QuotationSelectionTableModel> =
    new PagedResponseModel<QuotationSelectionTableModel>();
  dataQuotationDetail: PagedResponseModel<QuotationDetailSelectionTableModel> =
    new PagedResponseModel<QuotationDetailSelectionTableModel>();
  filtersBreakdown: ColumnResponseModel[] = [];
  filtersQuotation: ColumnResponseModel[] = [];
  filtersQuotationDetail: ColumnResponseModel[] = [];
  internalOrderModel: InternalOrderModel = new InternalOrderModel();
  limit: number = 10;
  loading: boolean = false;
  page: number = 1;
  quotationDetailHeader: ColumnResponseModel[] = [];
  quotationDetailId: number | null = null;
  quotationHeader: ColumnResponseModel[] = [];
  quotationId: number | null = null;
  readonly stepPanel = StepPanelEnums;
  subscription: Subscription;
  subscriptionParameters: Subscription;
  swShowOnlyOrderBreakdown: boolean = false;

  async ngOnInit() {
    this.subscription = this.config.data?.inputs$?.subscribe((inputs: any) => {
      this.internalOrderModel = inputs.internalOrderModel;
    });
    this.subscriptionParameters = this.userAuthenticatedService.generalParameterObservable.subscribe(response => {
      this.swShowOnlyOrderBreakdown = response.some(x => x.name === GeneralParameters.swShowOnlyOrderBreakdown);
    });
    this.loading = true;
    await this.getDataForStep(StepPanelEnums.PanelOne);
    setTimeout(() => this.loading = false, 300);
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.subscriptionParameters?.unsubscribe();
  }

  private clearFutureStepSelections(currentStep: number) {
    switch (currentStep) {
      case StepPanelEnums.PanelOne:
        this.quotationId = null;
        break;
      case StepPanelEnums.PanelTwo:
        if (!this.swShowOnlyOrderBreakdown)
          this.quotationDetailId = null;
        break;
    }
  }

  costCenterRowClass = (item: { costCenterId?: number | null }): string =>
    this.isSameCostCenter(item) ? 'highlight-row' : '';

  async getBreakdownForSelection() {
    try {
      this.dataBreakdown = await firstValueFrom(this.breakdownService.getBreakdownByDetailId(
        this.page, this.limit, this.columnSort, this.ascendant, this.filtersBreakdown,
        this.quotationDetailId ?? 0, this.quotationId ?? 0, 0, this.internalOrderModel.internalOrderId));
    } catch (error) {
      this.dataBreakdown = new PagedResponseModel<BreakdownByDetailTableModel>();
      console.error('Error al obtener la lista de los artículos', error);
    }
  }

  async getColumnsForQuotation() {
    try {
      this.quotationHeader = await firstValueFrom(this.quotationService.getColumnsForQuotationSelection());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getColumnsForQuotationDetail() {
    try {
      this.quotationDetailHeader = await firstValueFrom(this.quotationDetailService.GetColumnsForQuotationDetailsSelection());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getColumnsForBreakdown() {
    try {
      this.breakdownHeader = await firstValueFrom(this.breakdownService.getColumnsBreakdownByDetailId());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getDataForStep(step: StepPanelEnums) {
    const loadQuotations = () => {
      this.filtersQuotation = [];
      return Promise.all([this.getColumnsForQuotation(), this.getQuotationsForSelection()]);
    };
    const loadQuotationDetails = () => {
      this.filtersQuotationDetail = [];
      return Promise.all([this.getColumnsForQuotationDetail(), this.getQuotationDetailsForSelection()]);
    };
    const loadBreakdowns = () => {
      this.filtersBreakdown = [];
      return Promise.all([this.getColumnsForBreakdown(), this.getBreakdownForSelection()]);
    };

    const actions: Record<number, () => Promise<any>> = this.swShowOnlyOrderBreakdown
      ? {
        [StepPanelEnums.PanelOne]: loadQuotations,
        [StepPanelEnums.PanelTwo]: loadBreakdowns,
      }
      : {
        [StepPanelEnums.PanelOne]: loadQuotations,
        [StepPanelEnums.PanelTwo]: loadQuotationDetails,
        [StepPanelEnums.PanelThree]: loadBreakdowns,
      };

    try {
      if (actions[step]) await actions[step]();
    } catch (error) {
      console.error(`Error cargando datos del ${StepPanelEnums[step]}:`, error);
    }
  }

  async getQuotationsForSelection() {
    try {
      this.dataQuotation = await firstValueFrom(this.quotationService.getQuotationsForSelection(
        this.page, this.limit, this.columnSort, this.ascendant, this.filtersQuotation, true));
    } catch (error) {
      console.error('Error al obtener la lista de cotizaciones.', error);
    }
  }

  async getQuotationDetailsForSelection() {
    try {
      this.dataQuotationDetail = await firstValueFrom(this.quotationDetailService.getQuotationDetailsForSelection(
        this.page, this.limit, this.columnSort, this.ascendant, this.filtersQuotationDetail, this.quotationId ?? 0));
    } catch (error) {
      this.dataQuotationDetail = new PagedResponseModel<QuotationDetailSelectionTableModel>();
      console.error('Error al obtener los detalles de cotización.', error);
    }
  }

  getQuotationRestriction(quotation: { quotationStateId: number, clientId?: number }): string | null {
    if (quotation.quotationStateId != QuotationStateEnums.approved)
      return 'La cotización seleccionada debe estar aprobada para poder continuar.';
    if (quotation.clientId !== this.internalOrderModel.maintenanceOrder?.clientId)
      return 'La cotización seleccionada debe ser del mismo cliente de la orden de mantenimiento.';
    return null;
  }

  async goToStep(step: number, activateCallback: any) {
    if (step < this.currentStep) this.clearFutureStepSelections(step);
    if (!this.validateCurrentStepSelection(step)) return;
    this.page = 1;
    await this.getDataForStep(step);
    this.currentStep = step;
    activateCallback(step);
  }

  async onAction(event: { action: string; item?: BreakdownByDetailTableModel[]; selectedItems?: number[] }) {
    const { action, item, selectedItems } = event;
    if (action !== 'add' || !selectedItems || selectedItems.length === 0) return;
    if (this.swShowOnlyOrderBreakdown && item?.some(breakdown => !this.isSameCostCenter(breakdown))) {
      this.toastHandlerService.showToast(
        'warning',
        'Los artículos seleccionados deben ser de soluciones del mismo centro de costos de la orden de mantenimiento.'
      );
      return;
    }
    try {
      await firstValueFrom(this.internalOrderDetailService.createByQuotation(selectedItems, this.internalOrderModel.internalOrderId));
      this.toastHandlerService.showToast(
        'success',
        'El detalle del pedido interno fue creado exitosamente.'
      );
      const data: ModalObjectType = { params: { reloadDetail: true } };
      this.ref?.close(data);
    } catch (error) {
      console.error('Error al crear el detalle del pedido interno.', error);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;

    if (this.currentStep === StepPanelEnums.PanelOne) {
      await this.getQuotationsForSelection();
    } else if (this.currentStep === StepPanelEnums.PanelTwo && !this.swShowOnlyOrderBreakdown) {
      await this.getQuotationDetailsForSelection();
    } else {
      await this.getBreakdownForSelection();
    }
  }

  onFilterChange(event: { filters: any[] }) {
    const filters = event.filters.map((value: any, index: number) => ({ value, index }))
      .sort((a, b) => b.index - a.index).map(item => item.value)
      .filter((value, index, self) => index === self.findIndex((t: any) => t.propertyName === value.propertyName))
      .filter((f: any) => f.valueFilter !== null && f.valueFilter !== undefined && f.valueFilter !== '' && f.valueFilter !== 0);

    if (this.currentStep === StepPanelEnums.PanelOne) {
      this.filtersQuotation = filters;
    } else if (this.currentStep === StepPanelEnums.PanelTwo && !this.swShowOnlyOrderBreakdown) {
      this.filtersQuotationDetail = filters;
    } else {
      this.filtersBreakdown = filters;
    }
  }

  quotationRowClass = (item: QuotationSelectionTableModel): string =>
    this.getQuotationRestriction(item) ? '' : 'highlight-row';

  isSameCostCenter(item: { costCenterId?: number | null }): boolean {
    const costCenterId = this.internalOrderModel.maintenanceOrder?.costCenterId;
    return !!costCenterId && item.costCenterId === costCenterId;
  }

  selectionChange(event: { selectedItem?: any }) {
    const selectedId = event?.selectedItem?.id ?? null;
    if (this.currentStep === StepPanelEnums.PanelOne) {
      const restriction = selectedId ? this.getQuotationRestriction(event.selectedItem) : null;
      if (restriction) {
        this.quotationId = null;
        this.toastHandlerService.showToast('warning', restriction);
        return;
      }
      this.quotationId = selectedId;
    } else if (this.currentStep === StepPanelEnums.PanelTwo && !this.swShowOnlyOrderBreakdown) {
      if (selectedId && !this.isSameCostCenter(event.selectedItem)) {
        this.quotationDetailId = null;
        this.toastHandlerService.showToast(
          'warning',
          'La solución seleccionada debe ser del mismo centro de costos de la orden de mantenimiento.'
        );
        return;
      }
      this.quotationDetailId = selectedId;
    }
  }

  validateCurrentStepSelection(step: number): boolean {
    if (step === StepPanelEnums.PanelTwo && !this.quotationId) {
      this.toastHandlerService.showToast(
        'warning',
        this.swShowOnlyOrderBreakdown
          ? 'Debe seleccionar una cotización válida antes de avanzar al despiece.'
          : 'Debe seleccionar una cotización válida antes de avanzar a la solución.'
      );
      return false;
    }
    if (step === StepPanelEnums.PanelThree && !this.quotationDetailId) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe seleccionar una solución válida antes de avanzar al despiece.'
      );
      return false;
    }
    return true;
  }
}
